import { z } from "zod";
import { ClientId, DateSchema } from "../../Types";
import { GoalBasedFinancialPlanGoalIdSchema, GoalBasedFinancialPlanIdSchema, } from "../ids";
import { GoalBasedFinancialPlanAnnualIncreaseInputSchema, GoalBasedFinancialPlanAnnualIncreaseResponseSchema, GoalBasedPlanDateRequest, GoalBasedPlanDateResponse, GoalBasedRepeatTimeFrameSchema, } from "../types";
const DateOfBirthType = z
    .string()
    .refine((val) => val.match(/^\d{4}\/\d{2}\/\d{2}$/), "Date format must be: YYYY/MM/DD");
export const GoalBasedGoalTypes = [
    "AssetPurchase",
    "Vehicle",
    "CashReserve",
    "Education",
    "Gift",
    "HomeImprovement",
    "HomePurchase",
    "InvestmentProperty",
    "Land",
    "Legacy",
    "Other",
    "Retirement",
    "Vacation",
    "VacationHome",
    "Wedding",
];
export const GoalBasedGoalTypeSchema = z.enum(GoalBasedGoalTypes);
export const GoalBasedGoalTypeLabels = [
    "Asset Purchase",
    "Vehicle",
    "Cash Reserve",
    "Education",
    "Gift",
    "Home Improvement",
    "Home Purchase",
    "Investment Property",
    "Land",
    "Legacy",
    "Other",
    "Retirement",
    "Vacation",
    "Vacation Home",
    "Wedding",
];
export const GoalBasedGoalTypeLabelSchema = z.enum(GoalBasedGoalTypeLabels);
export const GoalBasedGoalTypeMap = {
    AssetPurchase: "Asset Purchase",
    Vehicle: "Vehicle",
    CashReserve: "Cash Reserve",
    Education: "Education",
    Gift: "Gift",
    HomeImprovement: "Home Improvement",
    HomePurchase: "Home Purchase",
    InvestmentProperty: "Investment Property",
    Land: "Land",
    Legacy: "Legacy",
    Other: "Other",
    Retirement: "Retirement",
    Vacation: "Vacation",
    VacationHome: "Vacation Home",
    Wedding: "Wedding",
};
export const GoalBasedFinancingMethodTypes = ["Cash", "Lease", "Loan"];
export const GoalBasedFinancingMethodSchema = z.enum(GoalBasedFinancingMethodTypes);
export const GoalBasedFinancingTypeSchema = z.enum([
    "downPayment",
    "interestRate",
    "term",
    "residualValue",
    "costOfSale",
    "yearOfSale",
]);
export const GoalBasedFinancingRecord = z.record(GoalBasedFinancingTypeSchema, z.string());
// Request
const BaseGoal = z.object({
    goalType: GoalBasedGoalTypeSchema,
    owner: ClientId,
    name: z.string(),
    amount: z.string().optional(),
    startDate: GoalBasedPlanDateRequest.nullish(),
    endDate: GoalBasedPlanDateRequest.nullish(),
    repeats: GoalBasedRepeatTimeFrameSchema,
    financeMethod: GoalBasedFinancingMethodSchema,
});
const RetirementGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["Retirement"]),
    dateOfBirth: DateOfBirthType,
    financeMethod: z.literal("Cash"),
});
const AssetPurchaseGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["AssetPurchase"]),
    financing: GoalBasedFinancingRecord,
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
});
const HomePurchaseGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["HomePurchase"]),
    financing: GoalBasedFinancingRecord,
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
});
const InvestmentPropertyGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["InvestmentProperty"]),
    financing: GoalBasedFinancingRecord,
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
});
const LandGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["Land"]),
    financing: GoalBasedFinancingRecord,
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
});
const VacationHomeGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["VacationHome"]),
    financing: GoalBasedFinancingRecord,
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
});
const HomeImprovementGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["HomeImprovement"]),
    financing: GoalBasedFinancingRecord,
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
});
const CashReserveGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["CashReserve"]),
    financeMethod: z.literal("Cash"),
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
});
const EducationGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["Education"]),
    financeMethod: z.literal("Cash"),
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
});
const GiftGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["Gift"]),
    financeMethod: z.literal("Cash"),
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
});
const VacationGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["Vacation"]),
    financeMethod: z.literal("Cash"),
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
});
const WeddingGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["Wedding"]),
    financeMethod: z.literal("Cash"),
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
});
const OtherGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["Other"]),
    financeMethod: z.literal("Cash"),
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
});
const LegacyGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["Legacy"]),
    financeMethod: z.literal("Cash"),
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
});
const VehicleGoal = BaseGoal.extend({
    goalType: GoalBasedGoalTypeSchema.extract(["Vehicle"]),
    financing: GoalBasedFinancingRecord,
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
});
export const GoalBasedFinancialPlanGoalPostRequestSchema = z.discriminatedUnion("goalType", [
    RetirementGoal,
    AssetPurchaseGoal,
    HomePurchaseGoal,
    InvestmentPropertyGoal,
    LandGoal,
    VacationHomeGoal,
    HomeImprovementGoal,
    VehicleGoal,
    CashReserveGoal,
    EducationGoal,
    GiftGoal,
    VacationGoal,
    WeddingGoal,
    OtherGoal,
    LegacyGoal,
]);
export const GoalBasedFinancialPlanGoalPatchRequestSchema = z.discriminatedUnion("goalType", [
    RetirementGoal.partial().required({ goalType: true }),
    AssetPurchaseGoal.partial().required({ goalType: true }),
    HomePurchaseGoal.partial().required({ goalType: true }),
    LandGoal.partial().required({ goalType: true }),
    VacationHomeGoal.partial().required({ goalType: true }),
    HomeImprovementGoal.partial().required({ goalType: true }),
    VehicleGoal.partial().required({ goalType: true }),
    CashReserveGoal.partial().required({ goalType: true }),
    EducationGoal.partial().required({ goalType: true }),
    GiftGoal.partial().required({ goalType: true }),
    VacationGoal.partial().required({ goalType: true }),
    WeddingGoal.partial().required({ goalType: true }),
    OtherGoal.partial().required({ goalType: true }),
    LegacyGoal.partial().required({ goalType: true }),
    InvestmentPropertyGoal.partial().required({ goalType: true }),
]);
// Response
export const GoalBasedFinancialPlanGoalResponseSchema = z.object({
    goalId: GoalBasedFinancialPlanGoalIdSchema,
    goalBasedFinancialPlanId: GoalBasedFinancialPlanIdSchema,
    goalType: GoalBasedGoalTypeSchema,
    owner: ClientId,
    name: z.string(),
    amount: z.string().optional(),
    dateOfBirth: DateOfBirthType.optional(),
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseResponseSchema,
    financeMethod: GoalBasedFinancingMethodSchema,
    financing: GoalBasedFinancingRecord.optional(),
    startDate: GoalBasedPlanDateResponse.optional(),
    endDate: GoalBasedPlanDateResponse.optional(),
    repeats: GoalBasedRepeatTimeFrameSchema,
    createdAt: DateSchema,
    updatedAt: DateSchema,
});
export const GoalBasedFinancialPlanGoalDeleteResponseSchema = z.object({
    message: z.string(),
});
//# sourceMappingURL=types.js.map